

  import Vue from 'vue'
  import Component from "vue-class-component";
  import {namespace} from "vuex-class";
  import {Prop, Watch} from "vue-property-decorator";
  import {IOnOffHandlers} from "bonomo/dist";
  import {IShortServiceProvider} from "@/model/shortInterfaces";
  import ServiceProviderChip from "@/components/shared/chips/ServiceProviderChip.vue";
  import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";
  import ServiceProviderTypeChip from "@/components/shared/chips/ServiceProviderTypeChip.vue";
  import {BoPanel} from "bonomo";
  import ServiceProviderActiveWidget from "@/components/serviceProvider/widget/ServiceProviderActiveWidget.vue";
  import BoListFilters from "@/components/shared/BoListFilters.vue";
  import CommunityNav from "@/components/community/widget/CommunityNav.vue";
  import PaginationBar from "@/components/shared/PaginationBar.vue";
  import {communityServiceProviderService} from "@/services/communityServiceProviderService";
  import {PAGINATION_FIRST_PAGE, PAGINATION_DEFAULT_PER_PAGE, PAGINATION_PER_PAGE_OPTIONS} from "@/constants/pagination";
  import {extractApiErrorMessage} from "@/helpers/errorsHelper";

  const serviceProviderModule = namespace("serviceProviderModule");

  interface IProviderWithConnect extends IShortServiceProvider {
    connectModel: boolean;
    position_natural: number|null;
    channel_inconsistency: boolean;
  }

  const POSITION_NATURAL = 'position_natural'

  @Component({
    components: {
      BoListFilters,
      CommunityNav,
      ServiceProviderActiveWidget,
      BoPanel,
      ServiceProviderTypeChip,
      ServiceProviderChip,
      PaginationBar,
    }
  })
  export default class CommunityServiceProviders extends Vue {

    @Prop()
    protected communityId!: number;

    @serviceProviderModule.Getter('serviceProviders')
    private providers!: IShortServiceProvider[];

    protected connectedIds: number[] = [];
    protected inconsistentIds: number[] = [];
    protected connectionsLoading: boolean = true;
    protected loading: boolean = true;
    protected maxPosition: number = 0;
    protected onlyActive: boolean = false;
    protected onlyAssigned: boolean = true;
    protected providerItems: IProviderWithConnect[] = [];
    protected search: string = '';
    protected sortBy: string | string[] = POSITION_NATURAL;
    protected sortDesc: boolean | boolean[] = false;
    protected subscribes: IOnOffHandlers = {};

    protected currentPage: number = PAGINATION_FIRST_PAGE;
    protected perPage: number = PAGINATION_DEFAULT_PER_PAGE;
    protected lastPage: number = PAGINATION_FIRST_PAGE;
    protected total: number = 0;
    protected readonly perPageOptions: number[] = PAGINATION_PER_PAGE_OPTIONS;

    protected get headers(): BetterDataTableHeader[] {
      const ret: BetterDataTableHeader[] = [
        { text: this.$t('common.order'), align: 'start', value: POSITION_NATURAL },
        { text: this.$t('common.id'), align: 'start', sortable: true, value: 'id' },
        { text: this.$t('common.name'), align: 'start', value: 'name' },
        { text: this.$t('common.type'), align: 'start', sortable: true, value: 'type' },
        { text: this.$t('common.active'), align: 'center', value: 'active', sortable: false },
        { text: this.$t('common.assigned'), align: 'center', value: 'connectModel', sortable: false },
      ];
      return ret.filter(each => (
          (each.value !== 'type' || this.$vuetify.breakpoint.mdAndUp) &&
          (each.value !== 'active' || this.$vuetify.breakpoint.lgAndUp)
      ));
    }

    get combinedSearch() {
      // we make sure search is triggered even when search input is empty. otherwise our custom filter is not applied
      //  also A : B ensures that seach changes when the active/assigned switch changes
      return (this.search||'').toString() +
        (this.onlyAssigned ? 'A' : 'B') +
        (this.onlyActive ? 'A' : 'B');
    }

    @Watch('combinedSearch')
    onCombinedSearchChanged() {
      this.currentPage = PAGINATION_FIRST_PAGE;
    }

    created() {
      this.subscribes = this.$ons({
        'backend.call.OK.serviceProvidersLoaded': this.onProvidersLoaded,
        'backend.call.OK.listServiceProviderLoaded': this.onActiveUpdated,
      });
    }

    beforeDestroy() {
      this.$offs(this.subscribes);
    }

    async loadData() {
      this.loading = true;
      try {
        if (!this.providers || !this.providers.length) {
          this.$store.dispatch('serviceProviderModule/fetchServiceProviders');
        }
        else {
          this.onProvidersLoaded();
        }
        await this.fetchConnected();
      } finally {
        this.loading = false;
      }
    }

    private async fetchConnected() {
      this.connectionsLoading = true;
      try {
        const connected = await communityServiceProviderService.fetchByCommunity(this.communityId);
        this.connectedIds = connected.map(each => each.id);
        this.inconsistentIds = connected.filter(each => each.channel_inconsistency).map(each => each.id);
        this.makeProviderItems();
      } catch (e) {
        this.$uiBus.emit('alert', extractApiErrorMessage(e));
      } finally {
        this.connectionsLoading = false;
      }
    }

    protected customSort(items: IProviderWithConnect[], indexList: string[], isDesc: boolean[]) {
      items.sort((a: IProviderWithConnect, b: IProviderWithConnect) => {
        if (a.connectModel && !b.connectModel) return -1;
        if (b.connectModel && !a.connectModel) return 1;
        return indexList
          .map((index: string): number => {
            return this.compare(a, b, index, isDesc[indexList.indexOf(index)]);
          })
          .reduce((current: number, next: number): number => current !== 0 ? current : next, 0);
      });
      return items;
    }

    private compare(a: IProviderWithConnect, b: IProviderWithConnect, index: string, isDesc: boolean): number {
      let elA: IProviderWithConnect = a;
      let elB: IProviderWithConnect = b;

      const strcmp = (a: string, b: string): number => a === b ? 0 : (a < b ? -1 : 1);

      if (isDesc) {
        elA = b;
        elB = a;
      }

      switch (index) {
        case 'position_natural': {
          return elA['position_natural'] && elB['position_natural']
              ? elA['position_natural'] - elB['position_natural']
              : strcmp(elA['name'], elB['name']); // this applies a 2nd sort by name for not assigned providers
        }
        default: {
          return strcmp((elA as any)[index], (elB as any)[index]);
        }
      }

    }

    private makeProviderItems() {
      this.providerItems.forEach(
          (each) => {
            each.connectModel = this.hasProvider(each.id);
            each.position_natural = this.providerOrder(each.id);
            each.channel_inconsistency = this.inconsistentIds.indexOf(each.id) !== -1;
          }
      );
    }

    protected providersFilter(
      value: any,
      search: string,
      item: IProviderWithConnect
    ) {
      if (item.connectModel) {
        return true;
      }
      const notBySearch = this.search.length &&
        (value||'').toString().toLowerCase().indexOf(this.search.toLowerCase()) === -1
      const notByActive = this.onlyActive && !item.active;
      const notByAssigned = this.onlyAssigned && !item.connectModel;
      const notByDeleted = item.deleted_at && !item.connectModel;
      return !notBySearch && !notByActive && !notByAssigned && !notByDeleted;
    }

    private hasProvider(providerId: number) {
      return this.connectedIds && (this.connectedIds.indexOf(providerId) !== -1);
    }

    protected onMoveUp(item: IProviderWithConnect) {
      this.moveProvider(item.id, communityServiceProviderService.moveUp);
    }

    protected onMoveDown(item: IProviderWithConnect) {
      this.moveProvider(item.id, communityServiceProviderService.moveDown);
    }

    private async moveProvider(serviceProviderId: number, move: (communityId: number, serviceProviderId: number) => Promise<any>) {
      this.connectionsLoading = true;
      try {
        await move(this.communityId, serviceProviderId);
        await this.fetchConnected();
      } catch (e) {
        this.$uiBus.emit('alert', extractApiErrorMessage(e));
      } finally {
        this.connectionsLoading = false;
      }
    }

    private onProvidersLoaded() {
      this.maxPosition = 0;
      this.providerItems = this.providers.map(
          each => Object.assign({}, each, { connectModel: false, position_natural: null, channel_inconsistency: false })
      );
      this.makeProviderItems();
    }

    protected async onSwitch(serviceProviderId: number, connect: boolean) {
      this.connectionsLoading = true;
      try {
        if (connect) {
          await communityServiceProviderService.create(this.communityId, serviceProviderId);
          this.notifyAssigned(serviceProviderId);
        }
        else {
          await communityServiceProviderService.destroy(this.communityId, serviceProviderId);
        }
        await this.fetchConnected();
      } catch (e) {
        this.$uiBus.emit('alert', extractApiErrorMessage(e));
      } finally {
        this.connectionsLoading = false;
      }
    }

    private notifyAssigned(serviceProviderId: number) {
      const provider = this.providerItems.find(each => each.id === serviceProviderId);
      this.$uiBus.emit('success', this.$t('communities.serviceProviders.assignedOK', { name: provider ? provider.name : '' }));
    }

    private onActiveUpdated(serviceProvider: IShortServiceProvider) {
      this.providerItems.forEach(each => {
        if (each.id == serviceProvider.id) {
          each.active = serviceProvider.active;
        }
      });
    }

    private providerOrder(providerId: number): number|null {
      if (this.connectedIds) {
        let providerOrder = this.connectedIds.indexOf(providerId);
        this.maxPosition = Math.max(this.maxPosition, providerOrder + 1);
        return providerOrder < 0 ? null : providerOrder + 1;
      }

      return null;
    }

    protected isRestricted(item: IProviderWithConnect): boolean {
      const limitedTo = item.limited_to;
      if (!limitedTo || !limitedTo.length) return false;
      return !limitedTo.map(Number).includes(this.communityId);
    }

    protected showMovers(item: IProviderWithConnect): boolean {
      return !this.sortDesc && this.sortBy === POSITION_NATURAL && !!item[POSITION_NATURAL];
    }

    protected onPagination(pagination: { pageCount: number; itemsLength: number }) {
      this.lastPage = Math.max(PAGINATION_FIRST_PAGE, pagination.pageCount);
      this.total = pagination.itemsLength;
    }

    protected onPageChange(page: number) {
      this.currentPage = page;
    }

    protected onPerPageChange(value: number) {
      this.perPage = value;
      this.currentPage = PAGINATION_FIRST_PAGE;
    }

  }
